/**
 * Council Trail Task 5.5 — useMessagesPeerMetadata (sibling-lookup bridge).
 *
 * Per-message peer-author metadata for MessageRow sibling chrome. Subscribes
 * to councilWsClient turn_added events and folds them into a
 * Map<messageId, PeerMetadata> keyed by JSONB message id.
 *
 * Resolution: event.source_persona_id → council.state.personasById[id] →
 * getPersonaColor(persona, color_map). If the roster row or persona is
 * unknown (race during initial hydrate), the event is silently dropped;
 * a later refresh + replay would pick it up if needed.
 */
import { useContext, useEffect, useState } from 'react';

import { CouncilContext } from '../contexts/CouncilContext';
import { councilWsClient } from '../services/councilWsClient';
import type { PeerEventWire, PeerMetadata } from '../types/council';
import { getPersonaColor } from '../utils/personaColor';

export function useMessagesPeerMetadata(
  sessionId: string,
): ReadonlyMap<string, PeerMetadata> {
  const [map, setMap] = useState<Map<string, PeerMetadata>>(() => new Map());
  const council = useContext(CouncilContext);

  useEffect(() => {
    const unsub = councilWsClient.subscribe(sessionId, (event: PeerEventWire) => {
      if (event.event_type !== 'turn_added' || !event.source_persona_id) return;
      const messageId = (event.target as any).message_id as string;
      if (!messageId) return;

      const personaId = event.source_persona_id;
      const state = council?.state ?? null;
      const rosterEntry = (state?.roster ?? [])
        .find((r: any) => r.persona_id === personaId);
      const persona = state?.personasById?.[personaId];
      if (!rosterEntry || !persona) return;

      const color = getPersonaColor(
        { id: personaId, accent_color: persona.accent_color, name: persona.name },
        state?.metadata?.persona_color_map,
      );
      setMap(prev => {
        const next = new Map(prev);
        next.set(messageId, {
          personaId,
          accentColor: color.paletteIndex,
          displayName: persona.name,
        });
        return next;
      });
    });
    return unsub;
  }, [sessionId, council]);

  return map;
}
